import { Play, X } from "lucide-react";
import { Card, CardContent } from "src/components/ui/Card";
import { useState, useEffect, useRef } from "react";

const videoCards = [
  {
    id: 1,
    heading: "Human Proctored Test",
    description: "Ensures fairness, measures students' thinking, and highlights strengths. It recommends a personalized learning plan.",
    videoSrc: "https://www.youtube.com/watch?v=8PR38Iwrf2U"
  },
  {
    id: 2,
    heading: "1:1 Academic Support",
    description: "Identifies student's needs, engages them interactively, and ensures the student clearly understands the concepts covered.",
    videoSrc: "https://www.youtube.com/watch?v=WarNydnjbbI"
  },
  {
    id: 3,
    heading: "Reinforcement Learning",
    description: "AI trainers evaluate progress across Bloom's Taxonomy dimensions, then provide targeted support in areas needing improvement.",
    videoSrc: "https://www.youtube.com/watch?v=PkwyZ_9DyW8"
  }
];

const WhatWeOffer = () => {
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const [currentAutoplayIndex, setCurrentAutoplayIndex] = useState(0);
  const [isAutoplayActive, setIsAutoplayActive] = useState(false);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const videoEndTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Video duration in milliseconds (approximate YouTube video lengths + buffer)
  const VIDEO_DURATION = 30000; // 30 seconds - adjust based on actual video lengths

  const startAutoplay = () => {
    if (isAutoplayActive) return;
    
    setIsAutoplayActive(true);
    setCurrentAutoplayIndex(0);
    setPlayingVideo(videoCards[0].id);
  };

  const stopAutoplay = () => {
    setIsAutoplayActive(false);
    setPlayingVideo(null);
    
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
    
    if (videoEndTimerRef.current) {
      clearTimeout(videoEndTimerRef.current);
      videoEndTimerRef.current = null;
    }
  };

  const scheduleNextVideo = () => {
    if (videoEndTimerRef.current) {
      clearTimeout(videoEndTimerRef.current);
    }
    
    videoEndTimerRef.current = setTimeout(() => {
      setCurrentAutoplayIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % videoCards.length;
        setPlayingVideo(videoCards[nextIndex].id);
        return nextIndex;
      });
    }, VIDEO_DURATION);
  };

  const handlePlayClick = (cardId: number) => {
    stopAutoplay();
    setPlayingVideo(cardId);
  };

  const handleCloseVideo = () => {
    stopAutoplay();
    setPlayingVideo(null);
  };

  // Start autoplay when component mounts
  useEffect(() => {
    startAutoplay();
    
    // Cleanup on unmount
    return () => {
      stopAutoplay();
    };
  }, []);

  // Schedule next video when a video starts playing during autoplay
  useEffect(() => {
    if (isAutoplayActive && playingVideo !== null) {
      scheduleNextVideo();
    }
    
    // Cleanup timer when video changes or autoplay stops
    return () => {
      if (videoEndTimerRef.current) {
        clearTimeout(videoEndTimerRef.current);
        videoEndTimerRef.current = null;
      }
    };
  }, [playingVideo, isAutoplayActive]);

  const getYouTubeEmbedUrl = (url: string, autoplay: boolean = false) => {
    const videoId = url.split('v=')[1]?.split('&')[0];
    const autoplayParam = autoplay ? '&autoplay=1&mute=1' : '';
    return `https://www.youtube.com/embed/${videoId}?enablejsapi=1${autoplayParam}`;
  };

  const getYouTubeThumbnailUrl = (url: string) => {
    const videoId = url.split('v=')[1]?.split('&')[0];
    if (!videoId) return '';
    // Try sddefault for better visibility (640x480) - no play button overlay
    return `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
  };

  return (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Train Young Minds With AI Researchers*
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          *Over 60% of our tutors have trained AI models using RLHF.
          </p>
        </div>

         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
           {videoCards.map((card) => (
             <div key={card.id} className="flex justify-center">
               {/* Outer Container - Light Pink Background */}
               <div 
                 className="bg-purple-100 rounded-lg shadow-lg w-full max-w-[510px]"
                 style={{ height: '530px' }}
               >
                 {/* Video Element Area - 60% with margins */}
                 <div 
                   className="relative m-4 overflow-hidden rounded-lg"
                   style={{ height: '286px' }} // 60% of 530px minus margins
                 >
                   {playingVideo === card.id ? (
                     <div className="relative w-full h-full">
                       <iframe
                         src={getYouTubeEmbedUrl(card.videoSrc, isAutoplayActive)}
                         title={card.heading}
                         className="w-full h-full"
                         allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                         allowFullScreen
                       />
                       <button
                         onClick={handleCloseVideo}
                         className="absolute top-2 right-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-1 transition-all duration-300"
                       >
                         <X className="w-4 h-4" />
                       </button>
                     </div>
                   ) : (
                     <div 
                       className="relative cursor-pointer group w-full h-full"
                       onClick={() => handlePlayClick(card.id)}
                     >
                       {/* Video Thumbnail */}
                       <img
                         src={getYouTubeThumbnailUrl(card.videoSrc)}
                         alt={card.heading}
                         className="w-full h-full object-cover rounded-lg"
                         style={{ display: 'block', position: 'relative', zIndex: 1 }}
                         onError={(e) => {
                           console.log('Thumbnail failed to load:', getYouTubeThumbnailUrl(card.videoSrc));
                           // Try alternative thumbnail formats
                           const videoId = card.videoSrc.split('v=')[1]?.split('&')[0];
                           if (videoId) {
                               const altUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                               console.log('Trying alternative URL:', altUrl);
                               e.currentTarget.src = altUrl;
                           } else {
                               // Show purple background if no thumbnail available
                               e.currentTarget.style.display = 'none';
                               e.currentTarget.nextElementSibling?.classList.remove('hidden');
                           }
                         }}
                         onLoad={() => {
                           console.log('Thumbnail loaded successfully:', getYouTubeThumbnailUrl(card.videoSrc));
                         }}
                       />
                       
                       {/* Fallback background - only shown if thumbnail fails */}
                       <div className="hidden absolute inset-0 bg-purple-200 group-hover:bg-purple-300 transition-colors duration-300 rounded-lg"></div>
                       
                       {/* Subtle Hover Overlay */}
                       <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 rounded-lg"></div>
                     </div>
                   )}
                 </div>

                 {/* Horizontal Separator with spacing */}
                 <div className="mx-4 my-2">
                   <div className="border-t border-gray-400"></div>
                 </div>

                 {/* Text Content Area - No background, no borders */}
                 <div 
                   className="px-6 pb-6 flex flex-col justify-between"
                   style={{ height: '212px' }} // Remaining space
                 >
                   <div className="space-y-3">
                     {/* Heading */}
                     <h3 className="text-xl font-bold text-gray-900">
                       {card.heading}
                     </h3>

                     {/* Description */}
                     <p className="text-gray-600 leading-relaxed text-sm">
                       {card.description}
                     </p>
                   </div>

                   {/* Action Icon - Bottom Right */}
                   <div className="flex justify-end">
                     <div className="w-8 h-8 bg-red-500 border-2 border-white rounded flex items-center justify-center hover:bg-red-600 transition-colors duration-300 cursor-pointer">
                       <span className="text-white font-bold text-lg">+</span>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           ))}
         </div>
      </div>
    </section>
  );
};

export default WhatWeOffer;
